// =====================================================================
// «$5,000» Y NO {"valor_minimo": 5000} · AC-13 (1 de octubre de 2026)
// =====================================================================
// La condición de una regla es JSON en la base (`regla_autorizacion.
// condicion`). Quien la escribe no tiene por qué saberlo: teclea «5000»,
// «$5,000» o «5,000.50» y esto lo traduce al JSON que la API ya espera.
//
// QUÉ CAMPO SE OFRECE LO DICE EL DATO, no una lista escrita aquí:
// `tipo_accion_autorizable.unidad` (DINERO / CANTIDAD / PORCENTAJE /
// NINGUNA). Las claves del JSON son las que lee el motor
// (`backend/app/autorizaciones.py:_casa_condicion`) y las mismas que usa
// `escala/ReglasDelGrupo.jsx`.
//
// Todo lo de aquí es puro —sin React y sin red— para poder probarlo.

export const UNIDADES = {
  DINERO: {
    campo: 'valor_minimo', etiqueta: 'A partir de cuántos pesos',
    ejemplo: '5,000', maximo: null,
  },
  CANTIDAD: {
    campo: 'cantidad_minima', etiqueta: 'A partir de cuántas piezas',
    ejemplo: '10', maximo: null,
  },
  PORCENTAJE: {
    campo: 'porcentaje_minimo', etiqueta: 'A partir de qué porcentaje',
    ejemplo: '10', maximo: 100,
  },
  NINGUNA: null,
};

const FORMATO_PESOS = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 2,
});
const FORMATO_NUMERO = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

/** Una cifra escrita en la unidad de su regla, para leerla. */
export function cifraLegible(valor, unidad) {
  if (unidad === 'DINERO') return FORMATO_PESOS.format(valor);
  if (unidad === 'PORCENTAJE') return `${FORMATO_NUMERO.format(valor)} %`;
  if (unidad === 'CANTIDAD') {
    return `${FORMATO_NUMERO.format(valor)} ${Number(valor) === 1 ? 'pieza' : 'piezas'}`;
  }
  return FORMATO_NUMERO.format(valor);
}

/** El umbral guardado en una condición, o null si la regla vale siempre. */
export function umbralDe(condicion, unidad) {
  const u = UNIDADES[unidad];
  if (!u || !condicion) return null;
  const v = condicion[u.campo];
  return v === undefined || v === null ? null : Number(v);
}

/** «A partir de $5,000» o «Siempre», como lo lee una persona. */
export function condicionLegible(condicion, unidad) {
  if (!UNIDADES[unidad]) return 'Siempre';
  const v = umbralDe(condicion, unidad);
  if (v === null) return 'Siempre, sin importar el importe';
  return `A partir de ${cifraLegible(v, unidad)}`;
}

/**
 * Lee lo que la persona tecleó en el campo del umbral.
 *
 * Devuelve `{ valor, error }`:
 *   · vacío       → `{ valor: null }` — significa «siempre».
 *   · «$5,000»    → `{ valor: 5000 }`.
 *   · «cinco mil» → `{ error: '…' }`, con qué hacer ahora.
 *
 * SE QUITAN el signo de pesos, el de porcentaje, los espacios y las comas
 * de los miles, que es como la gente escribe cifras en México. Lo demás
 * no se adivina: un texto que no es una cifra se frena con su motivo.
 */
export function leerUmbral(texto, unidad) {
  const u = UNIDADES[unidad];
  if (!u) return { valor: null, error: null };
  const limpio = String(texto ?? '').trim().replace(/[$\s%]/g, '').replace(/,/g, '');
  if (limpio === '') return { valor: null, error: null };
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) {
    return {
      valor: null,
      error: `Escribe solo la cifra, por ejemplo ${u.ejemplo}. Sin letras ni signo de menos.`,
    };
  }
  const valor = Number(limpio);
  if (unidad === 'CANTIDAD' && !Number.isInteger(valor)) {
    return { valor: null, error: 'Las piezas van enteras: escribe la cifra sin decimales.' };
  }
  if (u.maximo !== null && valor > u.maximo) {
    return { valor: null, error: `El porcentaje va de 0 a ${u.maximo}. Escribe una cifra menor.` };
  }
  return { valor, error: null };
}

/** La condición que espera la API para un umbral (null = siempre → `{}`). */
export function condicionPara(valor, unidad) {
  const u = UNIDADES[unidad];
  if (!u || valor === null || valor === undefined) return {};
  return { [u.campo]: valor };
}

/** El permiso que piden el GET y el PUT de las reglas (migración 243). */
export const PERMISO_REGLAS = 'autorizaciones.reglas';

/** Las horas para decidir: entero de 1 a 720, como las acepta la API. */
export function leerHoras(texto) {
  const limpio = String(texto ?? '').trim();
  if (!/^\d+$/.test(limpio) || Number(limpio) < 1 || Number(limpio) > 720) {
    return { valor: null, error: 'Escribe un número entero de horas, de 1 a 720 (30 días).' };
  }
  return { valor: Number(limpio), error: null };
}

/**
 * Lo que cambió respecto a la regla guardada, listo para el PUT de
 * siempre. Solo viaja lo que cambió: un campo igual no se manda.
 *
 * La condición viaja como la espera la API: `{"valor_minimo": 5000}`, o
 * `{}` para «siempre» (el servidor la guarda nula). Con unidad NINGUNA
 * no se manda nunca: esa acción no tiene importe.
 */
export function cambiosDeRegla(regla, { umbral, firmas, horas }) {
  const cambios = {};
  if (UNIDADES[regla.unidad]) {
    const lectura = leerUmbral(umbral, regla.unidad);
    if (!lectura.error && lectura.valor !== umbralDe(regla.condicion, regla.unidad)) {
      cambios.condicion = condicionPara(lectura.valor, regla.unidad);
    }
  }
  const nFirmas = Number(firmas);
  if ([1, 2, 3].includes(nFirmas) && nFirmas !== regla.aprobadores_requeridos) {
    cambios.aprobadores_requeridos = nFirmas;
  }
  const plazo = leerHoras(horas);
  if (!plazo.error && plazo.valor !== regla.plazo_horas) {
    cambios.plazo_horas = plazo.valor;
  }
  return cambios;
}

/** El texto con el que se rellena el campo al abrir una regla guardada. */
export function textoInicial(condicion, unidad) {
  const v = umbralDe(condicion, unidad);
  if (v === null) return '';
  return unidad === 'DINERO' ? FORMATO_NUMERO.format(v) : String(v);
}
